import { MenuItem, Select, TableCell, TableRow, TextField } from '@material-ui/core';
import { FunctionComponent, useMemo } from 'react';

import { DataTableColumn, FilterType, getRowColumnValue } from './dataTable';

type UseTableFilteringParams = {
    columns: DataTableColumn[];
    allRows?: {[key: string]: any}[];
    rows?: {[key: string]: any}[];
    filters: {[key: string]: string};
    totalRows?: number;
    showFilters: boolean;
};

type UseTableFilteringReturn = {
    filterOptions: {[key: string]: string[]};
    filteredRows: {[key: string]: any}[];
    displayTotalRows: number;
};

export const useTableFiltering = ({
    columns,
    allRows,
    rows,
    filters,
    totalRows,
    showFilters
}: UseTableFilteringParams): UseTableFilteringReturn => {
    const filterOptions = useMemo(() => {
        if (!showFilters || !allRows) {
            return {};
        }

        const options: {[key: string]: Set<string>} = {};
        
        allRows.forEach((row) => {
            columns.forEach((column) => {
                if (column.filterType === FilterType.SELECT) {
                    if (!options[column.field]) {
                        options[column.field] = new Set<string>();
                    }
                    
                    const rawValue = getRowColumnValue(row, column.field);
                    
                    // Apply format function if it exists (for enum fields like workType, task|status, etc.)
                    if (column.format) {
                        const formattedValue = column.format(rawValue);
                        if (formattedValue) {
                            options[column.field].add(String(formattedValue));
                        }
                    } else if (Array.isArray(rawValue)) {
                        rawValue.forEach((item: string) => {
                            if (item) {
                                options[column.field].add(String(item));
                            }
                        });
                    } else if (rawValue) {
                        options[column.field].add(String(rawValue));
                    }
                }
            });
        });
        
        const result: {[key: string]: string[]} = {};
        Object.keys(options).forEach((key) => {
            result[key] = Array.from(options[key]).sort();
        });
        
        return result;
    }, [ allRows, columns, showFilters ]);

    const hasActiveFilters = useMemo(() => {
        if (!filters) {
            return false;
        }
        return Object.values(filters).some((value) => value.trim() !== '');
    }, [ filters ]);

    const filteredRows = useMemo(() : {[key: string]: any}[] => {
        if (!hasActiveFilters) {
            return rows || allRows || [];
        }

        const rowsToFilter = allRows || rows || [];

        return rowsToFilter.filter((row) => {
            return columns.every((column) => {
                if (column.filterable === false) {
                    return true;
                }

                const filterValue = filters[column.field];
                if (!filterValue || filterValue.trim() === '') {
                    return true;
                }

                const rawValue = getRowColumnValue(row, column.field);
                
                if (column.filterType === FilterType.SELECT) {
                    // Apply format function if it exists for comparison
                    if (column.format) {
                        const formattedValue = column.format(rawValue);
                        return String(formattedValue) === filterValue;
                    } else if (Array.isArray(rawValue)) {
                        return rawValue.some((item: string) => String(item) === filterValue);
                    } else {
                        return String(rawValue) === filterValue;
                    }
                } else {
                    const formattedValue = column.format ? column.format(rawValue) : rawValue;
                    const searchText = filterValue.toLowerCase().trim();
                    
                    if (Array.isArray(rawValue)) {
                        const arrayString = rawValue.join(', ').toLowerCase();
                        return arrayString.includes(searchText);
                    }

                    const valueString = String(formattedValue || rawValue || '').toLowerCase();
                    return valueString.includes(searchText);
                }
            });
        });
    }, [ allRows, rows, filters, columns, hasActiveFilters ]);

    const displayTotalRows = hasActiveFilters && allRows
        ? filteredRows.length
        : (totalRows ?? filteredRows.length);

    return {
        filterOptions,
        filteredRows,
        displayTotalRows
    };
};

type Props = {
    columns: DataTableColumn[];
    filters: {[key: string]: string};
    onFilterChange: (field: string, value: string) => void;
    allRows?: {[key: string]: any}[];
    rows?: {[key: string]: any}[];
    showFilters: boolean;
    stickyTopPx: number;
    classes: {
        filterRow: string;
        filterCell: string;
    };
};

const FilterRow: FunctionComponent<Props> = ({
    columns,
    filters,
    onFilterChange,
    allRows,
    rows,
    showFilters,
    stickyTopPx,
    classes
}: Props) => {
    const { filterOptions } = useTableFiltering({
        columns,
        allRows,
        rows,
        filters,
        showFilters
    });
    const renderFilterInput = (column: DataTableColumn) => {
        if (column.filterable === false) {
            return null;
        }

        if (column.filterType === FilterType.SELECT) {
            return (
                <Select
                    value={filters[column.field] || ''}
                    onChange={(e) => onFilterChange(column.field, e.target.value as string)}
                    displayEmpty
                    fullWidth
                    variant="outlined"
                >
                    <MenuItem value="">
                        <em>All</em>
                    </MenuItem>
                    {(filterOptions[column.field] || []).map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            );
        }

        return (
            <TextField
                size="small"
                placeholder="Filter..."
                value={filters[column.field] || ''}
                onChange={(e) => onFilterChange(column.field, e.target.value)}
                variant="outlined"
                fullWidth
            />
        );
    };

    return (
        <TableRow className={classes.filterRow}>
            {columns.map((column) => (
                <TableCell
                    key={`filter-${column.field}`}
                    className={classes.filterCell}
                    style={{ minWidth: column.minWidth, top: stickyTopPx }}
                >
                    {renderFilterInput(column)}
                </TableCell>
            ))}
        </TableRow>
    );
};

export default FilterRow;

